import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Zap, X, Loader2, CheckCircle, AlertCircle, Play, Download, Upload, Link2, ArrowUpRight, Eye } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { blobToFile } from '../../utils/mediaUtils';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, RegenerateButton, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import SendToTimelineButton from '../SendToTimelineButton';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, inputNeed } from '../../utils/nodeRequirements';

const UpscaleNode = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [imageLocal, setImageLocal] = useState(null);
  const [scale] = useState(2); // Fixed 2x upscale via Recraft
  
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const edges = useWorkflowStore((state) => state.edges);
  const nodes = useWorkflowStore((state) => state.nodes);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);
  
  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';
  const { openImagePreview } = useMediaPreview();

  // Check for connected input
  const inputEdge = edges.find((e) => e.target === id && e.targetHandle === 'input');
  
  const getConnectedNodeData = (edge) => {
    if (!edge) return null;
    const sourceNode = nodes.find((n) => n.id === edge.source);
    return sourceNode?.data;
  };
  
  const inputData = getConnectedNodeData(inputEdge);
  
  // Determine which file to use
  const imageSource = inputData?.hasOutput ? { 
    file: inputData.file, 
    preview: inputData.outputUrl || inputData.preview,
    name: inputData.fileName || 'connected_image',
    fromConnection: true
  } : imageLocal;

  // Chronometer effect
  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => {
        setChronometer((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleImageChange = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageLocal({ file, preview: e.target.result, name: file.name });
    };
    reader.readAsDataURL(file);
  };

  const handleSourcePick = useCallback(() => {
    setSourcePickerHandler((result) => {
      if (result.file) {
        handleImageChange(result.file);
      } else if (result.url) {
        setImageLocal({ file: null, preview: result.url, name: result.fileName, fromGallery: true });
      }
    });
    openSourcePicker(id, ['image'], 'image/jpeg,image/jpg,image/png,image/webp');
  }, [id, openSourcePicker]);

  const fetchFileFromUrl = async (url, filename) => blobToFile(url, filename, 'image/jpeg');

  const handleProcess = useCallback(async () => {
    let imageFile = imageSource?.file;
    if (!imageFile && imageSource?.preview) {
      // If preview is already an HTTP URL (R2), pass directly
      if (imageSource.preview.startsWith('http')) {
        resetExec();
        const result = await processNode({
          resultType: 'upscale',
          params: { image_url: imageSource.preview, scale },
        });
        if (result?.outputUrl) {
          updateNodeData(id, { hasOutput: true, outputUrl: result.outputUrl, assetId: result.assetId ?? null, outputType: 'image', processedAt: new Date().toISOString() });
          addAsset({ id: `asset_${Date.now()}`, type: 'image', tool: 'upscale', filename: `upscaled_${imageSource?.name || 'image'}`, url: result.outputUrl, createdAt: new Date().toISOString() });
          return true;
        }
        return false;
      }
      imageFile = await fetchFileFromUrl(imageSource.preview, imageSource.name);
    }
    if (!imageFile) return false;
    resetExec();

    const result = await processNode({
      resultType: 'upscale',
      files: [{ key: 'image_url', file: imageFile }],
      params: { scale },
    });

    if (result?.outputUrl) {
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        outputType: 'image',
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'image',
        tool: 'upscale',
        filename: `upscaled_${imageSource?.name || 'image'}`,
        url: result.outputUrl,
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [id, imageSource, scale, updateNodeData, addAsset, processNode, resetExec]);

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);
  const handleRegenerate = useCallback(() => {
    setReconfigure(false);
    updateNodeData(id, { hasOutput: false, outputUrl: null });
  }, [id, updateNodeData, setReconfigure]);
  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  const hasValidImage = imageSource?.preview || imageSource?.file || (inputData?.hasOutput);

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([inputNeed('image', !!hasValidImage, { connected: !!inputEdge })]);

  return (
    <div className={`node-container upscale-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`} data-testid="upscale-node">
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />
      {reconfigure && (
        <ReconfigureNotice
          previewUrl={data.thumbnailUrl || null}
          onCancel={() => setReconfigure(false)}
          testId="upscale-reconfigure-notice"
        />
      )}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="node-handle input-handle"
      />
      
      {/* Header */}
      <div className="node-header upscale-header">
        <div className="node-header-left">
          <Zap size={16} />
          <span>Upscale</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)}>
          <X size={14} />
        </button>
      </div>
      
      {/* Content */}
      <div className="node-content upscale-content">
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">{formatTime(chronometer)}</div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>Escalando imagen...</span>
            </div>
            <p className="node-processing-hint">Recraft Crisp Upscale</p>
          </div>
        ) : !showForm ? (
          <div className="node-result">
            <img 
              src={data.outputUrl} 
              alt="Upscaled" 
              className="node-result-image clickable"
              onClick={() => openImagePreview(data.outputUrl, 'Imagen Escalada HD')}
            />
            <div className="node-result-actions">
              <RegenerateButton onClick={handleRegenerate} testId="upscale-regen-btn" />
              <ReconfigureButton onClick={() => setReconfigure(true)} testId="upscale-reconfigure-btn" />
              <button 
                className="node-mini-preview-btn"
                onClick={() => openImagePreview(data.outputUrl, 'Imagen Escalada HD')}
              >
                <Eye size={12} />
                Ver
              </button>
              <button 
                className="node-mini-download-btn"
                onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `upscaled_${Date.now()}.png`); }}
              >
                <Download size={12} />
                Descargar HD
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="image" label="upscale" testIdPrefix="upscale-timeline" />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} />
              <span>Imagen escalada</span>
            </div>
          </div>
        ) : (
          <>
            {/* Image Input */}
            <div className="upscale-input-area">
              {imageSource?.preview ? (
                <div className="upscale-preview">
                  <img src={imageSource.preview} alt="To upscale" />
                  {!imageSource.fromConnection && (
                    <button className="upscale-clear-btn" onClick={() => setImageLocal(null)}>
                      <X size={12} />
                    </button>
                  )}
                  {imageSource.fromConnection && (
                    <div className="upscale-connected-badge">
                      <Link2 size={10} />
                    </div>
                  )}
                  <div className="upscale-preview-label">
                    <ArrowUpRight size={12} />
                    Lista para escalar
                  </div>
                </div>
              ) : inputEdge ? (
                <div className="upscale-waiting">
                  <Link2 size={24} />
                  <span>Esperando imagen...</span>
                </div>
              ) : (
                <div className="upscale-dropzone">
                  <Zap size={28} className="upscale-icon" />
                  <span className="upscale-text">Sube una imagen</span>
                  <span className="upscale-hint">JPG, PNG, WebP</span>
                  <button type="button" className="node-load-btn" onClick={handleSourcePick} data-testid="load-btn-upscale">
                    <Upload size={12} /> Cargar
                  </button>
                </div>
              )}
            </div>

            {/* Info */}
            <p className="upscale-info">
              Aumenta resolución y nitidez con Recraft AI
            </p>

            {/* Process Button */}
            <button
              className="node-process-btn upscale-process-btn"
              onClick={handleProcess}
              disabled={!hasValidImage}
              title={requirementsTitle(runMissing)}
              data-testid="upscale-generate-btn"
            >
              <Zap size={14} />
              Escalar Imagen
            </button>
            <NodeRequirements missing={runMissing} />
          </>
        )}

        <NodeError error={execError} insufficientCredits={insufficientCredits} />
      </div>
      
      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

UpscaleNode.displayName = 'UpscaleNode';

export default UpscaleNode;
